import { Injectable, NgZone } from '@angular/core';
import { Observable, ReplaySubject, from, bindCallback, defer } from 'rxjs';
import { share } from 'rxjs/operators';

import GoogleUser = gapi.auth2.GoogleUser;
import GoogleAuth = gapi.auth2.GoogleAuth;

import { ScriptLoaderService } from './script-loader.service';

const DEBUG = false;

/** Constants used in the gapi init params */
const GOOGLE_PROJECT_ID = '338719905965-s2n31ac6jk0a17qf1846lrbh6804rc28.apps.googleusercontent.com';
const DISCOVERY_DOCS: string[] = ['https://www.googleapis.com/discovery/v1/apis/tasks/v1/rest'];
const SCOPE: string = ['https://www.googleapis.com/auth/tasks', 'https://www.googleapis.com/auth/tasks.readonly'].join(' ');
const TOKEN_STORAGE_KEY = 'oauth_token';

@Injectable({
  providedIn: 'root'
})
export class AuthApiService {

  googleAuth$ = new ReplaySubject<GoogleAuth>(1);
  googleUser$ = defer(() => new Observable<GoogleUser>(obs => {
    this.googleAuth$.subscribe(auth => {
      obs.next(auth.currentUser.get());
      auth.currentUser.listen(value => this.ngZone.run(() => obs.next(value)));
    });
  })).pipe(share());
  signedIn$ = defer(() => new Observable<boolean>(obs => {
    this.googleAuth$.subscribe(auth => {
      obs.next(auth.isSignedIn.get());
      auth.isSignedIn.listen(value => this.ngZone.run(() => obs.next(value)));
    });
  }).pipe(share()));
  private fromInstance$: Observable<GoogleAuth> = defer(() => from(gapi.auth2.getAuthInstance()));

  constructor(private scriptLoader: ScriptLoaderService, private ngZone: NgZone) {
    if (DEBUG) {console.log('new AuthApiService()');}
    this.scriptLoader.onLoaded$.subscribe(() => {
      bindCallback<string,void>(gapi.load)('client:auth2').subscribe(() => {
        from(gapi.auth2.init(gapiAuthConfig)).subscribe(auth => {
          if (DEBUG) {console.log('gapi.auth2.init() complete');}
          this.ngZone.run(() => this.googleAuth$.next(auth));
        },
        err => console.error(err))
      });
    });
  }

  /**
   * Get the current user by authenticating via {@link GoogleAuth.signIn()}.
   * Cache the user's access_token in session storage.
   *
   * @see {GoogleAuthService.getAuth(boolean)}
   * @see {gapi.auth2.GoogleUser}
   */
  signIn(): void {
    if (DEBUG) {console.log('AuthApiService signIn()');}
    this.fromInstance$.subscribe((auth: GoogleAuth) => {
      auth.signIn().then((user: GoogleUser) => {
        this.ngZone.run(() => {
          if (DEBUG) {console.log('setting auth key');}
          sessionStorage.setItem(TOKEN_STORAGE_KEY, user.getAuthResponse().access_token);
        });
        if (DEBUG) {console.log('signed in');}
      },
      (err)  => {console.error(err);});
    });
  }

  /**
   * Forwards call to {@link GoogleAuthService#signOut()},
   * then erases this.user and the value of "accessToken" in session storage.
   *
   * @see {@link GoogleAuthService#signOut()}.
   */
  signOut(): void {
    if (DEBUG) {console.log('AuthApiService signOut()');}
    this.fromInstance$.subscribe((auth: GoogleAuth) => {
      auth.signOut().then(() => {
        this.ngZone.run(() => sessionStorage.removeItem(TOKEN_STORAGE_KEY));
        if (DEBUG) {console.log('signed out');}
      });
    });
  }
}

interface GoogleAuthConfig extends gapi.auth2.ClientConfig {
  discoveryDocs: string[];
}

export const gapiAuthConfig: GoogleAuthConfig = {
  client_id: GOOGLE_PROJECT_ID,
  discoveryDocs: DISCOVERY_DOCS,
  scope: SCOPE,
  ux_mode: 'popup',
  fetch_basic_profile: false,
  // cookie_policy: '',
  // hosted_domain: ''
};
